import React, { useEffect, useState } from "react";
import { ShieldAlert, Users, Activity, ScanLine } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/time";
import { Label, Stars, RiskBadge, StatCard, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";

const SIGNAL_LABELS = {
  duplicate_session: "Duplicate (same session)",
  high_velocity: "High velocity (many staff fast)",
  device_cluster: "Device cluster (one phone, many ratings)",
  ip_burst: "IP burst",
  repeat_device_employee: "Same phone, same employee",
  self_review_suspected: "Possible self-manipulation",
  rapid_succession: "Rapid succession",
  repeat_visitor: "Repeat visitor (same browser scanned repeatedly)",
  identical_comment: "Identical comment already posted recently",
  outside_hours: "Rated while the outlet was closed",
  uniform_pattern: "Long run of identical ratings for this employee",
};

export default function TrustCenter() {
  const [d, setD] = useState(null);
  const load = () => api.get("/trust/anomalies").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const moderate = async (f, status) => {
    try { await api.patch(`/feedback/${f.id}/moderate`, { review_status: status, reason: `Trust review → ${status}` }); toast.success(`Marked ${status.replace("_", " ")}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!d) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6">
      <div><Label>Credibility</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Trust Center</h1></div>

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Flagged for Review" value={d.flagged_count} sub={`${d.risk_pct}% of all feedback`} accent="#F59E0B" testId="trust-flagged" />
        <StatCard label="Total Feedback" value={d.total.toLocaleString()} testId="trust-total" />
        <StatCard label="Suspicious Clusters" value={d.session_clusters.length + d.device_clusters.length + (d.repeat_scanners?.length || 0)} sub="Sessions, devices & repeat scans" accent="#EF4444" testId="trust-clusters" />
      </div>

      {d.repeat_scanners?.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="repeat-scanners-panel">
          <div className="flex items-center gap-2"><ScanLine className="h-4 w-4 text-amber-500" /><Label>Repeat scanners (same browser, repeated QR/NFC scans)</Label></div>
          <p className="mt-1 text-xs text-slate-400">A single pseudonymous browser/app installation that scanned one employee's code {`\u2265`}3 times. Counted even if no rating was submitted.</p>
          <div className="mt-3 space-y-2">
            {d.repeat_scanners.map((s, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-50 p-2.5 text-sm" data-testid={`repeat-scanner-${i}`}>
                <div className="flex items-center gap-3">
                  <code className="text-xs text-slate-500">…{s.visitor}</code>
                  <span className="font-semibold text-slate-800">{s.employee_name || "Unknown"}</span>
                </div>
                <span className="text-slate-600">{s.scans} scans · {(s.sources || []).map((x) => x.toUpperCase()).join(" / ") || "—"} · last {fmtDateTime(s.last_seen)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(d.session_clusters.length > 0 || d.device_clusters.length > 0) && (
        <div className="grid gap-6 lg:grid-cols-2">
          <ClusterPanel title="Session clusters" icon={Activity} rows={d.session_clusters} />
          <ClusterPanel title="Device clusters" icon={Users} rows={d.device_clusters} />
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <Label>Flagged Feedback (never auto-deleted)</Label>
        <div className="mt-3 space-y-3">
          {d.flagged.length === 0 ? <EmptyState icon={ShieldAlert} title="No flagged feedback" hint="The trust engine has not flagged anything." /> :
            d.flagged.map((f) => (
              <div key={f.id} className="rounded-lg border border-slate-100 p-3" data-testid={`flagged-${f.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <Stars value={f.rating} />
                    <span className="font-semibold text-slate-800">{f.employee_name}</span>
                    <RiskBadge status={f.review_status} />
                  </div>
                  <span className="text-xs text-slate-400">{fmtDateTime(f.created_at, f.location_id)}</span>
                </div>
                {f.trust_signals?.length > 0 && <p className="mt-2 text-xs text-amber-600">Signals: {f.trust_signals.map((s) => SIGNAL_LABELS[s] || s).join(", ")} · trust {f.trust_score}</p>}
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => moderate(f, "TRUSTED")} data-testid={`restore-${f.id}`}>Mark trusted</Button>
                  <Button size="sm" variant="outline" onClick={() => moderate(f, "REJECTED")} data-testid={`reject-${f.id}`} className="text-red-600">Reject</Button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function ClusterPanel({ title, icon: Icon, rows }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-slate-400" /><Label>{title}</Label></div>
      <div className="mt-3 space-y-2">
        {rows.length === 0 ? <p className="py-4 text-center text-sm text-slate-400">None detected</p> :
          rows.map((c) => (
            <div key={c.key} className="flex items-center justify-between rounded-lg bg-slate-50 p-2 text-sm">
              <code className="text-xs text-slate-500">{c.key.slice(0, 14)}…</code>
              <span className="text-slate-600">{c.count} submissions · {c.employees} emp · avg {c.avg_rating}★</span>
            </div>
          ))}
      </div>
    </div>
  );
}
